import {
  Book,
  BookOpen,
  Calculator,
  FlaskConical,
  Globe,
  Languages,
  Laptop,
  Music,
  Palette,
  Trophy,
} from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * How a subject is coloured wherever the timetable prints one.
 *
 * A subject is grouped once, here, and every screen that shows it reads the
 * same answer — the weekly grid's cards, the exam table's chips, and the legend
 * under the grid, which is built from the groups actually on screen rather than
 * from a list somebody typed. Before this, the legend named six categories
 * whether or not the institute taught any of them, and could not name a
 * seventh.
 *
 * Grouping is by name because that is all the institute gives us: `subjects`
 * carries a name, not a discipline. When a subject group lands in the database
 * this file becomes the fallback for a subject that has not been assigned one.
 */

export type SubjectGroup =
  | 'MATHEMATICS'
  | 'LANGUAGES'
  | 'ENGLISH'
  | 'SCIENCE'
  | 'COMPUTING'
  | 'LIBRARY'
  | 'ARTS'
  | 'SPORTS'
  | 'MUSIC'
  | 'SOCIAL'
  | 'OTHER';

/** What the legend calls each group. */
export const SUBJECT_GROUP_LABEL = {
  MATHEMATICS: 'Mathematics',
  LANGUAGES: 'Languages',
  ENGLISH: 'English',
  SCIENCE: 'Science',
  COMPUTING: 'Computing',
  LIBRARY: 'Library',
  ARTS: 'Art & craft',
  SPORTS: 'Sports & activities',
  MUSIC: 'Music & dance',
  SOCIAL: 'Social studies',
  OTHER: 'Other',
} as const satisfies Record<SubjectGroup, string>;

export interface SubjectTheme {
  /** The card's own background, border and text. */
  cardBg: string;
  /** The little tile the icon sits on. */
  iconBg: string;
  /** The legend's dot. */
  dotColor: string;
  icon: ReactNode;
}

const THEME = {
  MATHEMATICS: {
    cardBg:
      'bg-[#eff6ff] dark:bg-[#0c1a30] border-[#dbeafe] dark:border-[#1e3a6a] text-[#1e40af] dark:text-[#93c5fd]',
    iconBg:
      'bg-white dark:bg-[#071324] border border-blue-200/80 dark:border-blue-900/60 text-blue-600 dark:text-blue-400',
    dotColor: 'bg-blue-500',
    icon: <Calculator className="h-3.5 w-3.5" />,
  },
  LANGUAGES: {
    cardBg:
      'bg-[#f0fdf4] dark:bg-[#0c2317] border-[#dcfce7] dark:border-[#164e2d] text-[#166534] dark:text-[#86efac]',
    iconBg:
      'bg-white dark:bg-[#0a1a12] border border-emerald-200/80 dark:border-emerald-900/60 text-emerald-600 dark:text-emerald-400',
    dotColor: 'bg-emerald-500',
    icon: <Languages className="h-3.5 w-3.5" />,
  },
  ENGLISH: {
    cardBg:
      'bg-[#faf5ff] dark:bg-[#1a102e] border-[#f3e8ff] dark:border-[#3b1a64] text-[#6b21a8] dark:text-[#d8b4fe]',
    iconBg:
      'bg-white dark:bg-[#140b24] border border-purple-200/80 dark:border-purple-900/60 text-purple-600 dark:text-purple-400',
    dotColor: 'bg-purple-500',
    icon: <BookOpen className="h-3.5 w-3.5" />,
  },
  SCIENCE: {
    cardBg:
      'bg-[#f0fdfa] dark:bg-[#082422] border-[#ccfbf1] dark:border-[#134e4a] text-[#115e59] dark:text-[#5eead4]',
    iconBg:
      'bg-white dark:bg-[#051c1a] border border-teal-200/80 dark:border-teal-900/60 text-teal-600 dark:text-teal-400',
    dotColor: 'bg-teal-500',
    icon: <FlaskConical className="h-3.5 w-3.5" />,
  },
  COMPUTING: {
    cardBg:
      'bg-[#fff1f2] dark:bg-[#2b0c14] border-[#ffe4e6] dark:border-[#4c1220] text-[#9f1239] dark:text-[#fda4af]',
    iconBg:
      'bg-white dark:bg-[#20080f] border border-rose-200/80 dark:border-rose-900/60 text-rose-600 dark:text-rose-400',
    dotColor: 'bg-rose-500',
    icon: <Laptop className="h-3.5 w-3.5" />,
  },
  LIBRARY: {
    cardBg:
      'bg-[#eef2ff] dark:bg-[#121838] border-[#e0e7ff] dark:border-[#273269] text-[#3730a3] dark:text-[#a5b4fc]',
    iconBg:
      'bg-white dark:bg-[#0c102b] border border-indigo-200/80 dark:border-indigo-900/60 text-indigo-600 dark:text-indigo-400',
    dotColor: 'bg-indigo-500',
    icon: <Book className="h-3.5 w-3.5" />,
  },
  ARTS: {
    cardBg:
      'bg-[#fff7ed] dark:bg-[#2a160a] border-[#ffedd5] dark:border-[#4d250c] text-[#9a3412] dark:text-[#fdba74]',
    iconBg:
      'bg-white dark:bg-[#1f0f06] border border-orange-200/80 dark:border-orange-900/60 text-orange-600 dark:text-orange-400',
    dotColor: 'bg-orange-500',
    icon: <Palette className="h-3.5 w-3.5" />,
  },
  SPORTS: {
    cardBg:
      'bg-[#fef3c7] dark:bg-[#281e05] border-[#fde68a] dark:border-[#523d0a] text-[#b45309] dark:text-[#fde68a]',
    iconBg:
      'bg-white dark:bg-[#1e1603] border border-amber-200/80 dark:border-amber-900/60 text-amber-600 dark:text-amber-400',
    dotColor: 'bg-amber-500',
    icon: <Trophy className="h-3.5 w-3.5" />,
  },
  MUSIC: {
    cardBg:
      'bg-[#faf5ff] dark:bg-[#1a102e] border-[#f3e8ff] dark:border-[#3b1a64] text-[#6b21a8] dark:text-[#d8b4fe]',
    iconBg:
      'bg-white dark:bg-[#140b24] border border-purple-200/80 dark:border-purple-900/60 text-purple-600 dark:text-purple-400',
    dotColor: 'bg-purple-500',
    icon: <Music className="h-3.5 w-3.5" />,
  },
  SOCIAL: {
    cardBg:
      'bg-[#f5f3ff] dark:bg-[#191338] border-[#ede9fe] dark:border-[#342770] text-[#5b21b6] dark:text-[#c4b5fd]',
    iconBg:
      'bg-white dark:bg-[#120c2e] border border-violet-200/80 dark:border-violet-900/60 text-violet-600 dark:text-violet-400',
    dotColor: 'bg-violet-500',
    icon: <Globe className="h-3.5 w-3.5" />,
  },
  OTHER: {
    cardBg:
      'bg-[#f8fafc] dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#334155] text-[#334155] dark:text-[#94a3b8]',
    iconBg:
      'bg-white dark:bg-[#0b1120] border border-slate-200/80 dark:border-slate-800 text-slate-600 dark:text-slate-400',
    dotColor: 'bg-slate-500',
    icon: <BookOpen className="h-3.5 w-3.5" />,
  },
} as const satisfies Record<SubjectGroup, SubjectTheme>;

/**
 * Which group a subject name belongs to.
 *
 * Order matters: "English" is checked before the general language list so it
 * keeps its own colour, and the science words are checked before the two-letter
 * matches so "Physics" cannot be read as computing.
 */
export function subjectGroupOf(name: string): SubjectGroup {
  const words = name.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const has = (...needles: string[]) =>
    needles.some((needle) => words.some((word) => word.startsWith(needle)));

  // The short codes an Indian timetable is full of. Matched whole, because
  // "PE" as a prefix would also claim "Performing Arts".
  const code = words.join('');
  if (code === 'pe' || code === 'pt' || code === 'ped') return 'SPORTS';
  if (code === 'it' || code === 'ict') return 'COMPUTING';

  if (has('math', 'algeb', 'geomet', 'arithmet', 'numer')) return 'MATHEMATICS';
  if (has('english')) return 'ENGLISH';
  if (
    has(
      'telugu',
      'hindi',
      'kannada',
      'tamil',
      'malayalam',
      'marathi',
      'sanskrit',
      'bengali',
      'gujarati',
      'punjabi',
      'urdu',
      'odia',
      'assamese',
      'french',
      'german',
      'spanish',
      'lang',
    )
  ) {
    return 'LANGUAGES';
  }
  if (has('evs', 'environ', 'scien', 'physic', 'chem', 'bio', 'botany', 'zoology')) {
    return 'SCIENCE';
  }
  if (has('comput', 'coding', 'robot', 'informat')) return 'COMPUTING';
  if (has('librar', 'reading')) return 'LIBRARY';
  if (has('art', 'craft', 'draw', 'paint')) return 'ARTS';
  if (has('sport', 'games', 'yoga', 'fitness', 'physical', 'drill')) return 'SPORTS';
  if (has('music', 'dance', 'singing')) return 'MUSIC';
  if (has('social', 'histor', 'geograph', 'civic', 'sst', 'econom', 'polit')) return 'SOCIAL';

  return 'OTHER';
}

/** The colours and icon for one subject, by name. */
export function subjectTheme(name: string): SubjectTheme {
  return THEME[subjectGroupOf(name)];
}

/** The dot the legend prints for a group. */
export function subjectGroupDot(group: SubjectGroup): string {
  return THEME[group].dotColor;
}
